<template>
  <div class="relative">
    <button
      :class="[
        'border rounded px-2 flex items-center h-8 cursor-pointer transition-all duration-200',
        isRecording
          ? 'bg-red-100 border-red-300 text-red-600 hover:bg-red-200'
          : 'bg-white hover:bg-gray-100 border-gray-300',
      ]"
      :title="isRecording ? 'Stop Recording' : 'Start Speech to Text'"
      :disabled="!isSupported"
      @click="toggleRecording"
    >
      <Icon
        :name="isRecording ? 'lucide:mic-off' : 'lucide:mic'"
        :class="{ 'animate-pulse': isRecording }"
      />
    </button>

    <!-- Voice Commands Guide - Fixed at bottom -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition ease-out duration-200"
        enter-from-class="opacity-0 translate-y-4"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-4"
      >
        <div
          v-if="isRecording"
          class="fixed bottom-4 right-4 z-[9999] w-80 bg-white rounded-lg shadow-2xl border border-gray-200"
        >
          <!-- Header -->
          <div class="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-red-50 to-pink-50 border-b border-gray-200 rounded-t-lg">
            <div class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span class="text-sm font-semibold text-gray-700">Voice Commands</span>
            </div>

            <div class="flex items-center gap-x-2">
              <button
                class="text-gray-400 hover:text-gray-600 transition-colors"
                @click="minimiseTab()"
              >
                <Icon
                  :name="isMinimised ? 'mdi:window-maximize' : 'mdi:window-minimize'"
                  class="w-4 h-4"
                />
              </button>
              <button
                class="text-gray-400 hover:text-gray-600 transition-colors"
                @click="isRecording = false; recognition?.stop()"
              >
                <Icon
                  name="mdi:close"
                  class="w-4 h-4"
                />
              </button>
            </div>
          </div>

          <!-- Commands List - Compact -->
          <div
            v-if="!isMinimised"
            class="p-4 space-y-3 max-h-96 overflow-y-auto"
          >
            <!-- Quick Reference -->
            <!-- Punctuation Commands -->
            <div v-if="punctuationCommands.length > 0">
              <h4 class="text-xs font-semibold text-gray-500 uppercase mb-2">
                Punctuation
              </h4>
              <div class="grid grid-cols-2 gap-2 text-xs">
                <div
                  v-for="(item, index) in punctuationCommands"
                  :key="index"
                  class="flex items-center justify-between bg-gray-50 rounded px-2 py-1.5"
                >
                  <span class="text-gray-600">{{ item.command }}</span>
                  <span class="font-mono text-gray-800">{{ item.symbol }}</span>
                </div>
              </div>
            </div>

            <!-- Formatting Commands -->
            <div v-if="formattingCommands.length > 0">
              <h4 class="text-xs font-semibold text-gray-500 uppercase mb-2">
                Formatting
              </h4>
              <div class="space-y-1 text-xs">
                <div
                  v-for="(item, index) in formattingCommands"
                  :key="index"
                  class="flex items-center justify-between bg-gray-50 rounded px-2 py-1.5"
                >
                  <span class="text-gray-600">{{ item.command }}</span>
                  <Icon
                    v-if="item.icon"
                    :name="item.icon"
                    class="w-4 h-4 text-gray-800"
                  />
                  <span
                    v-else
                    class="font-mono text-gray-800"
                  >{{ item.symbol }}</span>
                </div>
              </div>
            </div>

            <!-- Tip -->
            <div class="flex items-start gap-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
              <Icon
                name="mdi:lightbulb-outline"
                class="w-4 h-4 flex-shrink-0 mt-0.5"
              />
              <span>Speak naturally - commands convert automatically</span>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, defineModel } from 'vue'

const editor = defineModel('editor')
const isRecording = ref(false)
const isSupported = ref(false) // if the browser supports speech recognition.
const recognition = ref<any>(null)
const { t } = useI18n()
let currentInterimLength = 0
const voiceCommands = {
  // Punctuation
  'full stop': '.',
  'comma': ',',
  'question mark': '?',
  'exclamation mark': '!',
  'colon': ':',
  'semicolon': ';',
  'apostrophe': '’',
  'dollar': '$',
  'underscore': '_',
  // Line breaks
  'line break': '<br>',
}
const punctuationCommands = computed(() => {
  return [
    { command: 'full stop', symbol: '.' },
    { command: 'comma', symbol: ',' },
    { command: 'question mark', symbol: '?' },
    { command: 'exclamation mark', symbol: '!' },
    { command: 'colon', symbol: ':' },
    { command: 'semicolon', symbol: ';' },
    { command: 'apostrophe', symbol: '’' },
    { command: 'dollar', symbol: '$' },
    { command: 'underscore', symbol: '_' },
  ]
})

const formattingCommands = computed(() => {
  return [
    { command: 'line break', symbol: '<br>', icon: 'mdi:keyboard-return' },

  ]
})
// Process voice commands into punctuation
const processVoiceCommands = (text: string): string => {
  let processed = text

  // Replace voice commands with actual punctuation
  Object.entries(voiceCommands).forEach(([command, symbol]) => {
    // Match command with word boundaries, case insensitive
    const regex = new RegExp(`\\b${command}\\b`, 'gi')
    processed = processed.replace(regex, symbol)
  })

  // Clean up spacing around punctuation
  processed = processed
    .replace(/\s+([.,!?;:])/g, '$1') // Remove space before punctuation
    .replace(/([.,!?;:])\s*([.,!?;:])/g, '$1 $2') // Add space between punctuation
    .replace(/\s+/g, ' ') // Normalize multiple spaces
    .trim()

  return processed
}

const capitaliseFirst = (text: string): string => {
  if (!text) return text
  return text.charAt(0).toUpperCase() + text.slice(1)
}

const isMinimised = ref(false)
const minimiseTab = () => {
  isMinimised.value = !isMinimised.value
}
// Smart capitalization
const smartCapitalise = (text: string): string => {
  if (!text) return text

  // Capitalize first letter
  let result = text.charAt(0).toUpperCase() + text.slice(1)

  // Capitalize after sentence-ending punctuation (. ! ?)
  result = result.replace(/([.!?]\s+)([a-z])/g, (match, punctuation, letter) => {
    return punctuation + letter.toUpperCase()
  })

  // Capitalize after line breaks
  result = result.replace(/(\n)([a-z])/g, (match, newline, letter) => {
    return newline + letter.toUpperCase()
  })

  // Capitalize after <br> tags
  result = result.replace(/(<br>)([a-z])/gi, (match, br, letter) => {
    return br + letter.toUpperCase()
  })

  return result
}

const insertTextToEditor = (text: string, isFinal: boolean = false) => {
  if (!editor.value) return

  const startPosition = editor.value.state.selection.from

  // Process voice commands
  const processedText = processVoiceCommands(text)
  // processedText = smartCapitalise(processedText)

  // Remove previous interim text if exists
  if (currentInterimLength > 0) {
    editor.value.chain()
      .setTextSelection({
        from: startPosition - currentInterimLength,
        to: startPosition,
      })
      .deleteSelection()
      .run()
  }

  // Insert new text
  editor.value.chain().focus().insertContent(processedText).run()

  const newPosition = editor.value.state.selection.from
  currentInterimLength = newPosition - (startPosition - currentInterimLength)
}
const finaliseText = (text: string) => {
  if (!editor.value) return

  const startPosition = editor.value.state.selection.from

  // Process voice commands
  let processedText = processVoiceCommands(text)

  // Capitalize only the first letter
  processedText = capitaliseFirst(processedText)

  // Remove previous interim text
  if (currentInterimLength > 0) {
    editor.value.chain()
      .setTextSelection({
        from: startPosition - currentInterimLength,
        to: startPosition,
      })
      .deleteSelection()
      .run()
  }

  // Insert final text with space
  editor.value.chain()
    .focus()
    .insertContent(processedText + ' ')
    .run()

  // Reset interim length
  currentInterimLength = 0
}

const toggleRecording = () => {
  if (!isSupported.value || !recognition.value) return

  if (isRecording.value) {
    recognition.value.stop()
  }
  else {
    currentInterimLength = 0
    recognition.value.start()
    isRecording.value = true
  }
}

onMounted(() => {
  if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
    isSupported.value = true

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    recognition.value = new SpeechRecognition()

    recognition.value.continuous = true
    recognition.value.interimResults = true // Enable interim results
    recognition.value.lang = t('common.localeCode')

    recognition.value.onresult = (event: any) => {
      let interimTranscript = ''
      let finalTranscript = ''

      // Process all results from the latest result index
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript

        if (event.results[i].isFinal) {
          finalTranscript += transcript
        }
        else {
          interimTranscript += transcript
        }
      }

      // Show interim results in real-time
      if (interimTranscript) {
        insertTextToEditor(interimTranscript)
      }

      // When speech is finalised, add space and reset
      if (finalTranscript) {
        finaliseText(finalTranscript)
      }
    }

    recognition.value.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error)
      isRecording.value = false
      currentInterimLength = 0
    }

    recognition.value.onend = () => {
      isRecording.value = false
      currentInterimLength = 0
    }
  }
  else {
    console.warn('Speech Recognition not supported')
  }
})

onUnmounted(() => {
  if (recognition.value && isRecording.value) {
    recognition.value.stop()
  }
})
</script>
